import {
    ReactElement,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useWebsocket } from '@/common/providers/websocket-provider';
import { Task } from '@/api';
import {
    FetchTasksQuery,
    useEnableTasksSockets,
    useFetchAllTasks
} from '../../queries';
import { useCompanyContext } from '@/companies/providers';
import { MapUserLocation } from '../../components/user-map-marker';
import { useTaskFilter } from '../task-filter-provider';
import { TaskDashboardContext } from './context';

type V2LocationModel = {
    coordinates: number[];
    altitude: number | null;
    speed: number | null;
    accuracy: number | null;
    timestamp: number;
    what3words: string | null;
    userId: string;
    taskId: string;
    teamId: string;
    companyId: string;
};

type TaskDashboardProviderProps = {
    readonly children?: ReactNode;
};
export function TaskDashboardProvider(
    props: TaskDashboardProviderProps
): ReactElement {
    const { children } = props;

    const { teamIds, userIds, deviceIds } = useTaskFilter();

    const { company } = useCompanyContext();

    /**
     * Tasks
     */

    useEnableTasksSockets(company.id);

    const query = useMemo(() => {
        const _query: FetchTasksQuery = {
            companyId: company.id,
            liveOnly: true
        };
        if (teamIds.size > 0) {
            _query.teamIds = Array.from(teamIds);
        }
        if (userIds.size > 0) {
            _query.userIds = Array.from(userIds);
            _query.useNewUserSystem = true;
        }
        if (deviceIds.size > 0) {
            _query.deviceIds = Array.from(deviceIds);
        }
        return _query;
    }, [company.id, teamIds, userIds, deviceIds]);

    const { tasks: unFilteredTasks, status } = useFetchAllTasks(query);
    const tasksMapRef = useRef<Map<string, Task>>(new Map());

    const tasks = useMemo(() => {
        tasksMapRef.current = new Map();
        return unFilteredTasks
            .filter((task) => {
                if (task.simpleStatus !== 'COMPLETED') {
                    tasksMapRef.current.set(task.id, task);
                    return true;
                } else {
                    return false;
                }
            })
            .sort((a, b) => {
                const statusRank = {
                    ALERTED: 0,
                    ACTIVE: 1,
                    COMPLETED: 2
                };

                const aStatus = statusRank[a.simpleStatus];
                const bStatus = statusRank[b.simpleStatus];
                if (aStatus !== bStatus) {
                    return aStatus - bStatus;
                }
                return (
                    new Date(b.startTime).getTime() -
                    new Date(a.startTime).getTime()
                );
            });
    }, [unFilteredTasks]);

    /**
     * Locations
     */
    type SocketLocationType = {
        lat: number;
        lng: number;
        alt?: number;
        acc?: number;
        ts: number;
    };
    const [socketLocationsMap, setSocketLocationsMap] = useState<
        Map<string, SocketLocationType>
    >(new Map());
    const { subscribe, unsubscribe, on, off } = useWebsocket();

    useEffect(() => {
        const ids = subscribe([
            { resource: `companies#${company.id}.tasks_locations` }
        ]);
        return () => {
            unsubscribe(ids);
        };
    }, [company.id, subscribe, unsubscribe]);

    useEffect(() => {
        const locationsCallback = (message: unknown) => {
            const { data } = message as {
                data: { locations: V2LocationModel[] };
            };
            if (data.locations.length === 0) {
                return;
            }

            const location = data.locations[0];
            if (!tasksMapRef.current.has(location.taskId)) {
                return;
            }

            setSocketLocationsMap((prev) => {
                const taskId = location.taskId;
                const prevLocation = prev.get(taskId);

                if (prevLocation && prevLocation.ts > location.timestamp) {
                    return prev;
                }
                const newMap = new Map(prev);
                newMap.set(taskId, {
                    lat: location.coordinates[0],
                    lng: location.coordinates[1],
                    alt: location.altitude ?? undefined,
                    acc: location.accuracy ?? undefined,
                    ts: new Date(location.timestamp).getTime()
                });
                return newMap;
            });
        };
        on('tasks_locations', locationsCallback);

        return () => {
            off('tasks_locations', locationsCallback);
        };
    }, [on, off]);

    const locations = useMemo(() => {
        const _locations: MapUserLocation[] = [];
        tasks.forEach((task) => {
            const taskLocation = task.location;
            const socketLocation = socketLocationsMap.get(task.id);

            let lat: number | undefined;
            let lng: number | undefined;
            let acc: number | undefined;

            if (taskLocation && socketLocation) {
                if (
                    new Date(taskLocation.ts).getTime() >
                    new Date(socketLocation.ts).getTime()
                ) {
                    lat = taskLocation.lat;
                    lng = taskLocation.lng;
                    acc = taskLocation.acc;
                } else {
                    lat = socketLocation.lat;
                    lng = socketLocation.lng;
                    acc = socketLocation.acc;
                }
            } else if (taskLocation) {
                lat = taskLocation.lat;
                lng = taskLocation.lng;
                acc = taskLocation.acc;
            } else if (socketLocation) {
                lat = socketLocation.lat;
                lng = socketLocation.lng;
                acc = socketLocation.acc;
            }

            if (lat && lng) {
                _locations.push({
                    image: task.displayInfo.image,
                    userId: task.user?.id,
                    taskId: task.id,
                    lat: lat,
                    lng: lng,
                    acc: acc
                });
            }
        });

        return _locations;
    }, [tasks, socketLocationsMap]);

    return (
        <TaskDashboardContext.Provider
            value={{
                tasks,
                locations,
                loading: status === 'pending'
            }}
        >
            {children}
        </TaskDashboardContext.Provider>
    );
}
